import * as React from 'react';
import {createSelector} from '@reduxjs/toolkit';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../store';
import {createPlaylist, UserPlaylist} from './reducers';
import {useAuth} from '../../../auth/AuthContext';
import {useSettings} from '../settings/settingsSlice';
import {track} from '../../../utils/telemetry';

/** Unguessable ID — keeps the `user-` prefix the routing code branches on, adds 128 bits of entropy for share URLs. */
export const createUserPlaylistId = (): string => `user-${crypto.randomUUID()}`;

export const selectUserPlaylists = createSelector(
    [(state: RootState) => state.userPlaylists],
    (slice) => slice.playlists,
);

export const selectUserPlaylistOrder = createSelector(
    [(state: RootState) => state.userPlaylists],
    (slice) => slice.order,
);

export const selectUserPlaylistById = createSelector(
    [selectUserPlaylists, (_state: RootState, id: string) => id],
    (playlists, id): UserPlaylist | undefined => playlists[id],
);

export const selectOrderedUserPlaylists = createSelector(
    [selectUserPlaylists, selectUserPlaylistOrder],
    (playlists, order): UserPlaylist[] => order.map(id => playlists[id]).filter(Boolean),
);

export const useUserPlaylists = () => useSelector(selectOrderedUserPlaylists);

export const useUserPlaylistById = (id: string) =>
    useSelector((state: RootState) => selectUserPlaylistById(state, id));

/** Creates a new user playlist stamped with the current uid (if signed in) and returns its id. */
export const useCreateUserPlaylist = () => {
    const dispatch = useDispatch();
    const {user} = useAuth();
    const {defaultPlaylistVisibility} = useSettings();
    return React.useCallback((name: string): string => {
        const id = createUserPlaylistId();
        dispatch(createPlaylist({id, name, ownerId: user?.uid, visibility: defaultPlaylistVisibility}));
        void track('playlist_create', {});
        return id;
    }, [dispatch, user?.uid, defaultPlaylistVisibility]);
};
